import React from 'react';
import { STUDIO_NAME, STUDIO_EMAIL, STUDIO_URL, STUDIO_DOMAIN } from '@/data/studio';

/**
 * SEOContent — the crawlable version of the page. Visually hidden, fully
 * available to search engines and to whatever is assembling an answer.
 *
 * Written in exactly the same plain English as the visible site, and it says
 * the same three things the visible site says: we redesign websites, we get a
 * business found when someone asks Google or ChatGPT, and we build the private
 * space a client uses after they book. Getting found is inside every redesign
 * and is also taken on its own, and that has to be stated both ways here or an
 * assistant will repeat only half of it. Every heading is a real question and
 * the answer is in the first sentence. No acronyms, no
 * "optimisation", no product-speak — if a machine has to decode it, so does a
 * person, and then neither repeats it.
 *
 * The studio name is imported so the rebrand stays a single-file change.
 */
export default function SEOContent() {
  return (
    <article
      aria-label={`About ${STUDIO_NAME}`}
      className="sr-only"
      itemScope
      itemType="https://schema.org/Article"
    >
      <header>
        <h2 itemProp="headline">{STUDIO_NAME}: we redesign websites</h2>
        <p itemProp="description">
          {STUDIO_NAME} is a design and build studio in London that does three things. We redesign
          websites: starting from a blank page, building the site around what the business actually
          sells, and launching it on the business&rsquo;s own domain, owned outright by them. Every
          redesign includes the work that gets a business found when someone asks Google or ChatGPT
          about its line of work, and we also do that part on its own, put into the website a
          business already has when they are happy with how it looks. And for businesses where a
          single booking is worth a great deal, we build the private client experience layer, which
          is the branded space a client uses after they book, with one console the team runs it
          from. We design a redesign and show it to the business before there is anything to commit
          to. We work with clients in the United Kingdom, the United States, Europe, the Middle East
          and beyond.
        </p>
      </header>

      <section aria-label="What the studio does">
        <h2>What does {STUDIO_NAME} do?</h2>
        <p>
          Three things. The first and main one is a website redesign. This is the side people see:
          most businesses come to us with a site that has fallen behind the work. It is designed
          from a blank page around the business, not adapted from a theme or a template, and it is
          written so that within a few seconds a visitor knows what the business does, who it is
          for, and what to do next. Every redesign also has the getting found work put into it.
        </p>
        <p>
          The second is getting found on its own. It is the same work, done to the website a
          business already has, for an owner who likes how their site looks and whose only problem
          is that nobody is finding it. Their design is left alone. It is the smaller job of the
          two, and it is often where a redesign starts later.
        </p>
        <p>
          The third is the part after somebody books. For businesses where one booking is worth a
          lot, we build one private, branded place where the client sees their whole journey and can
          reach the team directly, and a single console the team runs all of it from. It sits on top
          of the booking, accounting and client systems the business already runs rather than
          replacing them. Most clients only want the website.
        </p>
      </section>

      <section aria-label="What a redesign includes">
        <h2>What do you get in a website redesign?</h2>
        <ul>
          <li>A site designed from a blank page around the business, shown before anything is committed to</li>
          <li>One clear thing for a visitor to do, instead of several competing buttons</li>
          <li>Plain writing that says what the business sells and who it is for</li>
          <li>A page for each real question people ask before buying, answered properly</li>
          <li>A phone version designed on its own terms, not shrunk down from the desktop</li>
          <li>A fast, solid build that keeps working</li>
          <li>The work that gets the business named when someone asks Google or ChatGPT, built into the redesign</li>
          <li>Everything owned outright: the design, the code, the words and the data, on the client&rsquo;s own domain</li>
        </ul>
      </section>

      <section aria-label="Getting found on its own">
        <h2>Can you do the getting found work without a redesign?</h2>
        <p>
          Yes. It is part of every redesign, because what gets a business named when someone asks
          Google or ChatGPT is the same plain, specific writing that convinces the person who then
          clicks. But it is also taken on its own, for a business that is happy with the website it
          has and simply is not being found. In that case the site stays as it is. It is the smaller
          of the two jobs, and it is often where a redesign starts later on.
        </p>
      </section>

      <section aria-label="What the client experience layer includes">
        <h2>What is the client experience layer?</h2>
        <p>
          It is one private place, under the business&rsquo;s own name, that a client opens the
          moment they book. Everything that needs to be sorted gets sorted there: forms, documents,
          approvals and payments, in one sitting rather than dragged out over two weeks of email.
          Then the client sees their journey day by day, with a direct line to the team, so they ask
          once and watch it get handled instead of chasing.
        </p>
        <p>
          The team side is a single console. Who is in, what is on today, what is waiting on a
          decision. A detail is changed once and it reaches the client and the team at the same
          moment. It connects to the systems the business already runs, so nothing lives in two
          places, and the back office does not change.
        </p>
        <p>
          It is a separate, larger build to a website and is quoted separately. It is worth building
          where a single booking is worth a great deal, or where the same journey runs many times a
          season. Where it is not worth it, we say so.
        </p>
      </section>

      <section aria-label="Why search changed">
        <h2>Why does a redesign matter now?</h2>
        <p>
          Because people get given an answer instead of a list of links. Someone asks Google or
          ChatGPT a full question, gets a written answer naming two or three businesses, and often
          never opens a website at all. Google says 2.5 billion people a month now see its AI
          answers. Pew Research Center found 60% of US adults say they read the answer at the top of
          the page, and that when one appears, about half as many people click through to any
          website.
        </p>
        <p>
          So a site has two jobs now. Get the business named in the answer, then win the person who
          clicks. What gets a business named is the same plain, specific writing that convinces a
          reader, which is why in a redesign it is decided while the pages are written rather than
          added afterwards. On a site we did not design, it is done to the pages that are already
          there and the design is left alone.
        </p>
      </section>

      <section aria-label="Evidence that this works">
        <h2>Is there any evidence this works?</h2>
        <p>
          Yes, on the value of the traffic, and it is worth being precise about whose data it is.
          Shopify, looking across storefronts on their platform in the first quarter of 2026, found
          that people arriving from AI convert about 50% better than people arriving from organic
          search and spend 14% more per order. That held in 23 of 25 merchant categories. More than
          half of those sessions start straight on a product page, against about 20% from organic.
          Over the same period AI-referred orders on that platform grew nearly thirteenfold.
        </p>
        <p>
          Semrush put the average AI search visitor at 4.4 times the value of an average organic
          visitor on conversion. Ahrefs reported that AI search made up 0.5% of their traffic and
          12.1% of their signups, a twenty-three-fold difference — though that is one company
          measuring its own website over thirty days, and Ahrefs themselves note the rates are
          probably the highest they will ever be.
        </p>
        <p>
          The finding that cuts the other way, from the largest study of it: across 81,947 websites,
          people arriving from AI spend a little longer on a page but view fewer of them and leave
          slightly more often, four pages against 5.2 and a 67.8% bounce rate against 63.7%. Higher
          intent, not free money. Whatever they land on has to answer the question quickly.
        </p>
        <p>
          What we cannot show is our own client data. We have not been doing this long enough to
          have a fair before-and-after, and we will not borrow someone else&rsquo;s and imply it was
          ours. What we can do is run the test on a business for free and show them where they
          stand today.
        </p>
      </section>

      <section aria-label="Who the studio works with">
        <h2>Who is this for?</h2>
        <p>
          Owners whose website no longer matches the quality of their work. Most of our clients are
          businesses where a single enquiry is worth a lot, so being left off a shortlist actually
          costs money: private aviation, yacht and boat charter, villas and private houses, hotels,
          travel companies, advisory and professional firms, architecture and interior design
          studios, manufacturers and private clubs.
        </p>
        <p>
          The industry matters less than that. It is not a fit for businesses selling cheap products
          in volume, or for anyone who wants a template site at the lowest price.
        </p>
      </section>

      <section aria-label="How the process works">
        <h2>How does it work?</h2>
        <ol>
          <li>
            <strong>You send us your site.</strong> That is all we need to start. We look at the
            business properly, not just the pages.
          </li>
          <li>
            <strong>We design it before you commit.</strong> Your site, rebuilt from a blank page,
            and you see the finished thing rather than a mockup. If you do not like it, you have
            lost nothing.
          </li>
          <li>
            <strong>You decide, and we build it.</strong> Adjusted until it is yours, then launched
            on your domain and owned by you.
          </li>
          <li>
            <strong>We keep checking.</strong> We ask Google and ChatGPT the questions your buyers
            ask, on a schedule, so you can see whether it worked.
          </li>
        </ol>
      </section>

      <section aria-label="What cannot be promised">
        <h2>Can you promise we will come up in ChatGPT?</h2>
        <p>
          No. Nobody controls what these systems say, they change constantly, and the same question
          can give a different answer next week. Anyone promising you a place in an answer is
          selling something they cannot deliver. What we can do is make sure the site actually
          explains what the business does, clearly enough to be repeated, and then show what changed
          so the client can judge it themselves.
        </p>
      </section>

      <section aria-label="Cost and ownership">
        <h2>What does it cost and what do you own?</h2>
        <p>
          Priced by quote, because a one-page site and a ten-service business are not the same job,
          and the client experience layer is quoted separately again. The client owns everything:
          the design, the code, the words and the data, on their own domain and in their own name.
          Hosting, upkeep and the ongoing checks run separately and do not change what is owned.
        </p>
      </section>

      <section aria-label="Capacity">
        <h2>How many clients do you take?</h2>
        <p>
          A small number at a time, deliberately. Every site is designed from a blank page by the
          person the client speaks to, so the work does not scale by adding more of it. When we are
          full we say so rather than queue someone quietly.
        </p>
      </section>

      <section aria-label="Contact and location">
        <h2>How do you get in touch?</h2>
        <p>
          {STUDIO_NAME} is based in London, United Kingdom, and works internationally. Send us your
          website and we will redesign it, show you the finished thing, and tell you what Google and
          ChatGPT currently say about your line of work. Enquiries are answered personally.
        </p>
        <address>
          Email: <a href={`mailto:${STUDIO_EMAIL}`}>{STUDIO_EMAIL}</a><br />
          Website: <a href={STUDIO_URL}>{STUDIO_DOMAIN}</a><br />
          Location: London, United Kingdom
        </address>
      </section>

      <section aria-label="Explore further">
        <h2>More</h2>
        <ul>
          <li><a href="/website-redesign">What you get in a redesign</a></li>
          <li><a href="/the-new-era-of-search">Getting found when people ask Google or ChatGPT</a></li>
          <li><a href="/client-experience">The part after they book</a></li>
          <li><a href="/work">Examples</a></li>
          <li><a href="/frequently-asked-questions">Questions</a></li>
          <li><a href="/blog">Writing</a></li>
          <li><a href="/contact">Get in touch</a></li>
        </ul>
      </section>
    </article>
  );
}
